import { FeeStatus, IdentificationStatus, RegistrationStatus, RegistrationType } from "../../Base/AdoptionConstants";

const defaultFormValues = {
  registrationId: null,
  registrantContactInfo: {
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    phone: "",
    streetAddress1: "",
    streetAddress2: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
  },
  registrationType: "",
  active: true,
  receiveMatchInfo: null,
  receiveBirthCertificate: null,
  shareContactInfo: "",
  contactInfoToShare: "", // field only used for validation/errors
  intermediaryContact: false,
  contactInformation: {
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    phone: "",
    streetAddress1: "",
    streetAddress2: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
  },
  releaseBirthCertificate: null,
  shareMedicalHistory: null,
  noAdoptedMinorSiblingsInHousehold: null,
  adopteeBirthFirstName: "",
  adopteeBirthMiddleName: "",
  adopteeBirthLastName: "",
  adopteeAdoptiveFirstName: "",
  adopteeAdoptiveMiddleName: "",
  adopteeAdoptiveLastName: "",
  adopteeCurrentFirstName: "",
  adopteeCurrentMiddleName: "",
  adopteeCurrentLastName: "",
  knowExactBirthDate: null,
  birthDate: null,
  birthDateRangeStart: null,
  birthDateRangeEnd: null,
  birthCity: "",
  birthHospital: "",
  otherBirthLocation: "",
  birthSex: "",
  adoptionAgencyAttorney: "",
  adoptiveParent1FirstName: "",
  adoptiveParent1MiddleName: "",
  adoptiveParent1LastName: "",
  adoptiveParent2FirstName: "",
  adoptiveParent2MiddleName: "",
  adoptiveParent2LastName: "",
  birthParent1CertFirstName: "",
  birthParent1CertMiddleName: "",
  birthParent1CertLastName: "",
  birthParent1CurrentFirstName: "",
  birthParent1CurrentMiddleName: "",
  birthParent1CurrentLastName: "",
  birthParent2CertFirstName: "",
  birthParent2CertMiddleName: "",
  birthParent2CertLastName: "",
  birthParent2CurrentFirstName: "",
  birthParent2CurrentMiddleName: "",
  birthParent2CurrentLastName: "",
  agreeToDisclaimer: false,
  requireUpdateFee: false,
  agreeToUpdateFee: false,
  birthMotherAliasFirstName: "",
  birthMotherAliasMiddleName: "",
  birthMotherAliasLastName: "",
  verifyIdentityOption: "",
  paymentOption: "",
  lastSearchedDate: null,
};

export function getInitialFormValuesFromRegistration(registration) {
  const initFormValues = {
    ...defaultFormValues,
    ...registration,
    registrantContactInfo: {
      ...registration.registrant.contactInformation,
    },
  };

  if (registration.birthDate) {
    initFormValues.knowExactBirthDate = "yes";
  } else if (registration.birthDateRangeStart && registration.birthDateRangeEnd) {
    initFormValues.knowExactBirthDate = "no";
  }

  if (!registration.contactInformation) {
    initFormValues.shareContactInfo = "no";
  } else if (registration.intermediaryContact) {
    initFormValues.shareContactInfo = "intermediary";
  } else {
    initFormValues.shareContactInfo = "yes";
  }

  if (registration.identificationStatus === IdentificationStatus.PENDING_REVIEW_MAIL.value) {
    initFormValues.verifyIdentityOption = "mail";
  } else if (registration.identificationStatus === IdentificationStatus.PENDING_REVIEW_UPLOAD.value) {
    initFormValues.verifyIdentityOption = "upload";
  } else if (registration.identificationStatus === IdentificationStatus.VERIFIED.value) {
    initFormValues.verifyIdentityOption = "verified";
  }

  if (registration.feeStatus === FeeStatus.FEE_PENDING.value) {
    initFormValues.paymentOption = "mail";
  } else if (registration.feeStatus === FeeStatus.PREV_PAID_PENDING.value) {
    initFormValues.paymentOption = "previous";
  }

  // If they have already created a registration, they have answered no to this
  initFormValues.noAdoptedMinorSiblingsInHousehold = false;

  return initFormValues;
}

export function getInitialFormValues(registrationType, currentUserInfo, isPaperRegistration) {
  // Don't use current user's contact info for paper registration
  if (isPaperRegistration) {
    return {
      ...defaultFormValues,
      registrationType,
      isPaperRegistration,
    };
  }
  const initFormValues = {
    ...defaultFormValues,
    registrationType,
    isPaperRegistration,
    registrantContactInfo: {
      firstName: currentUserInfo.firstName ?? "",
      middleName: currentUserInfo.middleName ?? "",
      lastName: currentUserInfo.lastName ?? "",
      email: currentUserInfo.email ?? "",
      phone: currentUserInfo.phone ?? "",
      streetAddress1: currentUserInfo.streetAddress1 ?? "",
      streetAddress2: currentUserInfo.streetAddress2 ?? "",
      city: currentUserInfo.city ?? "",
      state: currentUserInfo.state ?? "",
      zipCode: currentUserInfo.zipCode ?? "",
      country: currentUserInfo.country ?? "",
    },

    // If registering as ADOPTEE, set adoptee current name to user's name
    adopteeCurrentFirstName: registrationType === RegistrationType.ADOPTEE.value ? currentUserInfo.firstName : "",
    adopteeCurrentMiddleName: registrationType === RegistrationType.ADOPTEE.value ? currentUserInfo.middleName : "",
    adopteeCurrentLastName: registrationType === RegistrationType.ADOPTEE.value ? currentUserInfo.lastName : "",

    // If registering as BIRTH_PARENT, set birth parent current name to user's name
    birthParent1CurrentFirstName:
      registrationType === RegistrationType.BIRTH_PARENT.value ? currentUserInfo.firstName : "",
    birthParent1CurrentMiddleName:
      registrationType === RegistrationType.BIRTH_PARENT.value ? currentUserInfo.middleName : "",
    birthParent1CurrentLastName:
      registrationType === RegistrationType.BIRTH_PARENT.value ? currentUserInfo.lastName : "",
  };

  return initFormValues;
}

function isEqualContactInfo(info1, info2) {
  return (
    info1.firstName === info2.firstName &&
    info1.middleName === info2.middleName &&
    info1.lastName === info2.lastName &&
    info1.email === info2.email &&
    info1.phone === info2.phone &&
    info1.email === info2.email &&
    info1.streetAddress1 === info2.streetAddress1 &&
    info1.streetAddress2 === info2.streetAddress2 &&
    info1.city === info2.city &&
    info1.state === info2.state &&
    info1.zipCode === info2.zipCode &&
    info1.country === info2.country
  );
}

export function getRegistrationFromFormValues(formValues) {
  const registration = { ...formValues };
  if (formValues.shareContactInfo === "no") {
    registration.contactInformation = null;
  }
  // If contactInfo matches the registrant's, just reuse the same entity
  else if (isEqualContactInfo(formValues.contactInformation, formValues.registrantContactInfo)) {
    registration.contactInformation = formValues.registrantContactInfo;
  }
  // If Registrant contact info has been changed from shared contact info, remove ID to force creation of new object
  else if (
    formValues.contactInformation?.contactInformationId === formValues.registrantContactInfo?.contactInformationId
  ) {
    registration.contactInformation.contactInformationId = null;
  }

  if (formValues.verifyIdentityOption === "mail") {
    registration.identificationStatus = IdentificationStatus.PENDING_REVIEW_MAIL.value;
  } else if (formValues.verifyIdentityOption === "upload") {
    registration.identificationStatus = IdentificationStatus.PENDING_REVIEW_UPLOAD.value;
  } else if (formValues.verifyIdentityOption === "verified") {
    registration.identificationStatus = IdentificationStatus.VERIFIED.value;
  }

  if (formValues.paymentOption === "mail") {
    registration.feeStatus = FeeStatus.FEE_PENDING.value;
  } else if (formValues.paymentOption === "previous") {
    registration.feeStatus = FeeStatus.PREV_PAID_PENDING.value;
  }

  if (formValues.agreeToUpdateFee) {
    registration.feeStatus = FeeStatus.UPDATE_FEE_PENDING.value;
    registration.registrationStatus = RegistrationStatus.NEEDS_RESEARCH.value;
  }

  return registration;
}
